import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { parseMatchTime } from "../../utils/dateUtils";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS, getRoleColor } from "./adminStyles";

const API = getBaseURL();
const TABS = ["Overview","Players","Lineup","Score","Points","Player Stats","Contests"];

const MatchPanelInner = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState("Overview");
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Overview state
  const [score, setScore] = useState({ team1_runs:0,team1_wickets:0,team1_overs:"0",team2_runs:0,team2_wickets:0,team2_overs:"0" });
  const [toss, setToss] = useState({ toss_winner:"", toss_decision:"bat" });
  const [saving, setSaving] = useState(false);

  const [playerForm, setPlayerForm] = useState({ name:"",role:"BAT",credit:9,image:"",team:"" });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [pointsUpdating, setPointsUpdating] = useState(false);
  const [statsSaving, setStatsSaving] = useState(false);
  const [releasingPrizes, setReleasingPrizes] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [tossSaving, setTossSaving] = useState(false);
  const [playerSaving, setPlayerSaving] = useState(false);

  const syncPlayers = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/players/sync/${matchId}`, {}, { headers });
      toast.success("Players synced from master database!");
      fetchAll();
    } catch(e) { 
      toast.error("Sync failed: " + (e.response?.data?.message || e.message)); 
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate("/winrox11-super-admin"); return; }
    fetchAll();
  }, [matchId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [mRes, pRes, cRes] = await Promise.all([
        axios.get(`${API}/matches/${matchId}`),
        axios.get(`${API}/admin/players/${matchId}`, { headers }),
        axios.get(`${API}/admin/contests/${matchId}`, { headers }),
      ]);
      const m = Array.isArray(mRes.data) ? mRes.data[0] : (mRes.data?.data || mRes.data);
      setMatch(m);
      setScore({ team1_runs:m.team1_runs||0,team1_wickets:m.team1_wickets||0,team1_overs:m.team1_overs||"0",team2_runs:m.team2_runs||0,team2_wickets:m.team2_wickets||0,team2_overs:m.team2_overs||"0", match_winner:m.match_winner||"" });
      setToss({ toss_winner:m.toss_winner||"", toss_decision:m.toss_decision||"bat" });
      let matchPlayers = pRes.data || [];
      // Auto-sync from master_players if no players loaded yet
      if (matchPlayers.length === 0) {
        try {
          await axios.post(`${API}/admin/players/sync/${matchId}`, {}, { headers });
          const reSynced = await axios.get(`${API}/admin/players/${matchId}`, { headers });
          matchPlayers = reSynced.data || [];
        } catch(syncErr) { /* silently ignore */ }
      }
      setPlayers(matchPlayers);
      setContests(cRes.data || []);
    } catch(e) { toast.error("Failed to load match data."); }
    finally { setLoading(false); }
  };

  const setStatus = async (status) => {
    setStatusUpdating(true);
    try {
      await axios.put(`${API}/admin/matches/${matchId}/status`, { status }, { headers });
      toast.success(`Match is now ${status.toUpperCase()}`);
      fetchAll();
    } catch(e) { toast.error("Status update failed: " + (e.response?.data?.error || e.message)); }
    finally { setStatusUpdating(false); }
  };

  const publishLineup = async () => {
    setPublishing(true);
    try {
      await axios.put(`${API}/admin/matches/${matchId}/lineup-announced`, {}, { headers });
      toast.success("📣 Lineup published to app! Non-playing players are now hidden.");
      fetchAll();
    } catch(e) { toast.error("Publish failed: " + (e.response?.data?.error || e.message)); }
    finally { setPublishing(false); }
  };

  const saveToss = async () => {
    if (!toss.toss_winner) { toast.warn("Select toss winner first."); return; }
    setTossSaving(true);
    try {
      await axios.put(`${API}/admin/matches/${matchId}/toss`, toss, { headers });
      toast.success("Toss saved!");
    } catch(e) { toast.error("Toss save failed."); }
    finally { setTossSaving(false); }
  };

  const saveScore = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/matches/${matchId}/score`, score, { headers });
      toast.success("Score updated!");
    } catch(e) { toast.error("Score update failed."); }
    setSaving(false);
  };

  const deleteContest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contest?")) return;
    try {
      await axios.delete(`${API}/admin/contests/${id}`, { headers });
      toast.success("Contest deleted successfully");
      fetchAll();
    } catch (e) { toast.error("Failed to delete contest"); }
  };

  const editContest = async (c) => {
    const fee = prompt(`Edit Entry Fee for ${c.title||c.name}:`, c.entry_fee);
    if (fee === null) return;
    const pool = prompt(`Edit Prize Pool for ${c.title||c.name}:`, c.first_prize || c.prize_pool);
    if (pool === null) return;
    try {
      await axios.put(`${API}/admin/contests/${c.id||c.contest_id}`, { entry_fee: fee, prize_pool: pool, first_prize: pool }, { headers });
      toast.success("Contest updated successfully");
      fetchAll();
    } catch (e) { toast.error("Failed to update contest"); }
  };

  const savePlayer = async (e) => {
    e.preventDefault();
    setPlayerSaving(true);
    const team = playerForm.team || match?.team1;
    try {
      if (editingPlayer) {
        // update existing match player — field is player_name
        await axios.put(`${API}/admin/players/match/${editingPlayer}`, {
          player_name: playerForm.name, role: playerForm.role,
          credit: playerForm.credit, image: playerForm.image
        }, { headers });
        toast.success("Player updated!");
      } else {
        // add to master_players then sync to match
        await axios.post(`${API}/admin/players`, {
          name: playerForm.name, team, role: playerForm.role,
          credit: playerForm.credit, image: playerForm.image
        }, { headers });
        // now sync master → match players
        await axios.post(`${API}/admin/players/sync/${matchId}`, {}, { headers });
        toast.success("Player added & synced!");
      }
      setEditingPlayer(null);
    } catch(e) { toast.error(e.response?.data?.error || "Failed to save player."); }
  };

  const togglePlaying = async (playerId, isPlaying) => {
    const all = players.map(p => p.player_id === playerId ? { ...p, is_playing: !p.is_playing } : p);
    setPlayers(all);
    const playing = all.filter(p=>p.is_playing).map(p=>p.player_id);
    try {
      await axios.put(`${API}/admin/players/playing11`, { match_id: matchId, playing_player_ids: playing }, { headers });
    } catch(e) { toast.error("Lineup save failed."); }
  };

  const setCaptain = async (playerId, type) => {
    try {
      await axios.put(`${API}/admin/players/match/${playerId}`, { [type]: true }, { headers });
      toast.success(`${type === "is_captain" ? "Captain" : "Vice Captain"} set!`);
      const pRes = await axios.get(`${API}/admin/players/${matchId}`, { headers });
      setPlayers(pRes.data || []);
    } catch(e) { toast.error("Failed."); }
  };

  const [showPreview, setShowPreview] = useState(false);

  const publishLineupConfirmed = async () => {
    try {
      await axios.put(`${API}/admin/players/playing11`, { 
        match_id: matchId, 
        playing_player_ids: players.filter(p=>p.is_playing).map(p=>p.player_id) 
      }, { headers });
      // Also publish the lineup announced flag
      await axios.put(`${API}/admin/matches/${matchId}/lineup-announced`, {}, { headers });
      toast.success("📣 Lineup Published Successfully!");
      setShowPreview(false);
      fetchAll();
    } catch(e) { toast.error("Failed to publish lineup."); }
  };

  const savePoints = async () => {
    setPointsUpdating(true);
    try {
      const pointsData = players.map(p => ({ player_id: p.player_id, points: p.points || 0 }));
      await axios.post(`${API}/admin/matches/${matchId}/points`, { pointsData }, { headers });
      toast.success("Player points updated successfully!");
    } catch(e) { 
      toast.error("Points update failed: " + (e.response?.data?.error || e.message)); 
    } finally {
      setPointsUpdating(false);
    }
  };

  const handlePlayerPointChange = (playerId, newPoints) => {
    setPlayers(prev => prev.map(p => p.player_id === playerId ? { ...p, points: newPoints } : p));
  };

  const handlePlayerStatChange = (playerId, field, value) => {
    setPlayers(prev => prev.map(p => p.player_id === playerId ? { ...p, [field]: value } : p));
  };

  const savePlayerStats = async () => {
    setStatsSaving(true);
    try {
      const statsData = players.map(p => ({
        player_id: p.player_id,
        runs: p.runs || 0,
        fours: p.fours || 0,
        sixes: p.sixes || 0,
        wickets: p.wickets || 0,
        catches: p.catches || 0,
        run_outs: p.run_outs || 0
      }));
      await axios.post(`${API}/admin/matches/${matchId}/stats`, { statsData }, { headers });
      toast.success("Player Stats updated successfully! ✅");
    } catch(e) { 
      toast.error("Stats update failed: " + (e.response?.data?.error || e.message)); 
    } finally {
      setStatsSaving(false);
    }
  };

  const releasePrizes = async () => {
    if (!window.confirm("⚠️ ATTENTION: This will calculate winners and credit REAL MONEY to users' wallets. This cannot be undone. Proceed?")) return;
    setReleasingPrizes(true);
    try {
      await axios.post(`${API}/admin/matches/${matchId}/release-prizes`, {}, { headers });
      toast.success("🏆 Prizes released successfully! Winners' wallets have been credited.");
      fetchAll();
    } catch(e) { 
      toast.error("Prize release failed: " + (e.response?.data?.error || e.message)); 
    } finally {
      setReleasingPrizes(false);
    }
  };

  if (loading || !match) return (
    <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <div style={{ color: COLORS.textMuted }}>Loading match...</div>
    </div>
  );

  const isLive = match.status === "live";
  const statusColor = isLive ? COLORS.green : match.status === "completed" ? COLORS.textMuted : COLORS.indigo;
  const t1 = (match.team1 || "").trim().toUpperCase();
  const t2 = (match.team2 || "").trim().toUpperCase();
  const team1P = players.filter(p => (p.team || "").trim().toUpperCase() === t1);
  const team2P = players.filter(p => (p.team || "").trim().toUpperCase() === t2);
  const playingCount = players.filter(p=>p.is_playing).length;
  const t1Playing = team1P.filter(p=>p.is_playing).length;
  const t2Playing = team2P.filter(p=>p.is_playing).length;

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={()=>navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Dashboard</button>

      {/* Match Header */}
      <div style={{ background:COLORS.card, border:`1px solid ${statusColor}30`, borderRadius:16, padding:"20px 24px", marginBottom:24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <div>
            <div style={{ fontSize:26, fontWeight:800, color:COLORS.text, letterSpacing:-1 }}>
              {match.team1} <span style={{ color:COLORS.textDim, fontSize:16 }}>vs</span> {match.team2}
            </div>
            <div style={{ fontSize:12, color:COLORS.textMuted, marginTop:4 }}>
              📅 {parseMatchTime(match).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
              {match.venue && <span style={{marginLeft:10}}>🏟️ {match.venue}</span>}
              {match.format && <span style={{marginLeft:10, background:COLORS.indigo+"20", color:COLORS.indigoLight, padding:"2px 8px", borderRadius:6, fontSize:10, fontWeight:700}}>{match.format}</span>}
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ ...S.badge(statusColor), fontSize:11, padding:"6px 14px", display:"flex", alignItems:"center", gap:6 }}>
              {isLive && <span style={{ width:6, height:6, borderRadius:"50%", background:COLORS.green, animation:"pulse 1.5s infinite", display:"inline-block" }}/>}
              {(match.status||"upcoming").toUpperCase()}
            </span>
            {match.status !== "live" && match.status !== "completed" && (
              <button 
                className="admin-btn-hover" 
                disabled={statusUpdating}
                onClick={()=>setStatus("live")} 
                style={{ background:COLORS.green+"20", border:`1px solid ${COLORS.green}40`, color:COLORS.green, borderRadius:10, padding:"8px 16px", fontWeight:700, fontSize:12, cursor:statusUpdating?"not-allowed":"pointer", opacity:statusUpdating?0.7:1 }}
              >
                {statusUpdating ? "⌛ Starting..." : "▶ Start Live"}
              </button>
            )}
            {match.status === "live" && (
              <button 
                className="admin-btn-hover" 
                disabled={statusUpdating}
                onClick={()=>setStatus("completed")} 
                style={{ background:COLORS.red+"15", border:`1px solid ${COLORS.red}30`, color:COLORS.red, borderRadius:10, padding:"8px 16px", fontWeight:700, fontSize:12, cursor:statusUpdating?"not-allowed":"pointer", opacity:statusUpdating?0.7:1 }}
              >
                {statusUpdating ? "⌛ Ending..." : "⏹ End Match"}
              </button>
            )}
            {match.status === "completed" && (
              <button 
                className="admin-btn-hover" 
                disabled={statusUpdating}
                onClick={()=>setStatus("upcoming")} 
                style={{ background:COLORS.textMuted+"15", border:`1px solid ${COLORS.cardBorder}`, color:COLORS.textMuted, borderRadius:10, padding:"8px 16px", fontWeight:700, fontSize:12, cursor:statusUpdating?"not-allowed":"pointer", opacity:statusUpdating?0.7:1 }}
              >
                {statusUpdating ? "⌛ Reverting..." : "↩ Revert"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:24, background:COLORS.card, borderRadius:12, padding:4, border:`1px solid ${COLORS.cardBorder}` }}>
        {TABS.map(t => (
          <button key={t} onClick={()=>setTab(t)} style={{ flex:1, padding:"10px 8px", background:tab===t?COLORS.indigo:"transparent", color:tab===t?"white":COLORS.textMuted, border:"none", borderRadius:10, cursor:"pointer", fontWeight:700, fontSize:11, letterSpacing:"0.5px", textTransform:"uppercase", transition:"all 0.15s", fontFamily:"'Inter',sans-serif" }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ─────────────────────────────────── */}
      {tab === "Overview" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
          {/* Match Info */}
          <div style={S.card}>
            <div style={S.cardTitle}>Match Information</div>
            <div style={{ display:"grid", gap:14 }}>
              {[["ID", matchId], ["Format", match.format || "T20"], ["Venue", match.venue || "TBA"], ["Players", `${players.length} synced`]].map(([l, v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", borderBottom:`1px solid ${COLORS.cardBorder}`, paddingBottom:8 }}>
                  <span style={{ fontSize:12, color:COLORS.textMuted }}>{l}</span>
                  <span style={{ fontSize:12, fontWeight:700, color:COLORS.text }}>{v}</span>
                </div>
              ))}
            </div>
            {players.length === 0 && (
              <div style={{ marginTop:20, padding:14, background:COLORS.amber+"15", border:`1px solid ${COLORS.amber}30`, borderRadius:10 }}>
                <div style={{ fontSize:13, fontWeight:700, color:COLORS.amber, marginBottom:4 }}>⚠️ No Players Found</div>
                <div style={{ fontSize:11, color:COLORS.textMuted }}>Click the sync button below to pull players from your 'Register Team' database.</div>
              </div>
            )}
            <button disabled={syncing} onClick={syncPlayers} style={{ ...S.btnPrimary, width:"100%", marginTop:20, background:syncing?COLORS.inputBg:COLORS.indigo }}>
              {syncing ? "🔄 Syncing..." : "🔄 Sync Players from Master"}
            </button>
          </div>

          {/* Toss */}
          <div style={S.card}>
            <div style={S.cardTitle}>🪙 Toss Control</div>
            <div style={{ marginBottom:14 }}>
              <label style={S.label}>Toss Winner</label>
              <select className="admin-input-focus" style={S.select} value={toss.toss_winner} onChange={e=>setToss({...toss,toss_winner:e.target.value})}>
                <option value="">-- Select --</option>
                <option value={match.team1}>{match.team1}</option>
                <option value={match.team2}>{match.team2}</option>
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={S.label}>Decision</label>
              <div style={{ display:"flex", gap:8 }}>
                {["bat","bowl"].map(d=>(
                  <button key={d} type="button" onClick={()=>setToss({...toss,toss_decision:d})} style={{ flex:1, padding:10, background:toss.toss_decision===d?COLORS.indigo+"25":COLORS.inputBg, border:`1px solid ${toss.toss_decision===d?COLORS.indigo+"60":COLORS.cardBorder}`, borderRadius:10, color:toss.toss_decision===d?COLORS.indigoLight:COLORS.textMuted, fontWeight:700, fontSize:12, cursor:"pointer", textTransform:"uppercase" }}>
                    {d==="bat"?"🏏 Bat":"⚡ Bowl"}
                  </button>
                ))}
              </div>
            </div>
            <button 
              className="admin-btn-hover" 
              disabled={tossSaving}
              onClick={saveToss} 
              style={{ ...S.btnPrimary, width:"100%", opacity:tossSaving?0.7:1, cursor:tossSaving?"not-allowed":"pointer" }}
            >
              {tossSaving ? "⌛ Saving Toss..." : "🪙 Save Toss"}
            </button>
            {match.toss_winner && (
              <div style={{ marginTop:10, fontSize:12, color:COLORS.green, textAlign:"center" }}>✓ {match.toss_winner} elected to {match.toss_decision}</div>
            )}
          </div>

          {/* Prize Management */}
          <div style={{ ...S.card, gridColumn: "span 2", border: `1px solid ${COLORS.amber}40`, background: COLORS.amber + "05" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ ...S.cardTitle, color: COLORS.amber, marginBottom: 4 }}>🏆 Prize Management</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>Distribute winnings to users based on current player points.</div>
              </div>
              <button 
                onClick={releasePrizes}
                disabled={releasingPrizes || match.status !== "completed"}
                style={{ 
                  ...S.btnPrimary, 
                  background: match.status === "completed" ? COLORS.amber : COLORS.inputBg,
                  color: match.status === "completed" ? "black" : COLORS.textMuted,
                  padding: "12px 24px",
                  fontSize: 13,
                  cursor: match.status === "completed" ? "pointer" : "not-allowed"
                }}
              >
                {releasingPrizes ? "⌛ Releasing..." : "💰 Release Prizes to Winners"}
              </button>
            </div>
            {match.status !== "completed" && (
              <div style={{ marginTop: 12, fontSize: 11, color: COLORS.amber, fontWeight: 600 }}>
                💡 Note: You must mark the match as 'Completed' (in Score tab) before releasing prizes.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PLAYERS TAB ─────────────────────────────────── */}
      {tab === "Players" && (
        <div style={{ paddingBottom: 40 }}>
          <div style={{ ...S.card, marginBottom: 30, background: "transparent", border: "none", padding: 0 }}>
             <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
               <div style={S.cardTitle}>🏏 SQUAD OVERVIEW</div>
               <div style={{ fontSize: 12, color: COLORS.indigoLight, fontWeight: 700, letterSpacing: 1 }}>{players.length} TOTAL PLAYERS</div>
             </div>
          </div>

          {["WK", "BAT", "AR", "BOWL"].map(roleKey => {
            const rolePlayers = players.filter(p => {
              const r = (p.role || "").toUpperCase();
              if (roleKey === "WK") return r.includes("WK") || r.includes("WICKET");
              if (roleKey === "BAT") return r.includes("BAT");
              if (roleKey === "AR") return r.includes("AR") || r.includes("ALL");
              if (roleKey === "BOWL") return r.includes("BOWL");
              return false;
            });

            if (rolePlayers.length === 0) return null;

            const roleTitle = roleKey === "WK" ? "Wicket Keepers" : roleKey === "BAT" ? "Batters" : roleKey === "AR" ? "All Rounders" : "Bowlers";
            const roleIcon = roleKey === "WK" ? "🧤" : roleKey === "BAT" ? "🏏" : roleKey === "AR" ? "🌟" : "⚡";

            return (
              <div key={roleKey} style={{ marginBottom: 40 }}>
                <div style={{ 
                  fontSize: 18, 
                  fontWeight: 800, 
                  color: COLORS.text, 
                  marginBottom: 20, 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 12,
                  borderBottom: `2px solid ${COLORS.indigo}30`,
                  paddingBottom: 10
                }}>
                  <span style={{ background: COLORS.indigo + "20", padding: "6px 10px", borderRadius: 8 }}>{roleIcon}</span>
                  {roleTitle}
                  <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: "auto" }}>{rolePlayers.length} Players</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 20 }}>
                  {rolePlayers.map(p => (
                    <div 
                      key={p.player_id} 
                      className="admin-card-hover"
                      style={{ 
                        background: COLORS.card, 
                        border: `1px solid ${COLORS.cardBorder}`, 
                        borderRadius: 16, 
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative"
                      }}
                    >
                      {/* Action Buttons Overlay */}
                      <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 4, zIndex: 10 }}>
                         <button 
                            onClick={() => {
                              setEditingPlayer(p.player_id);
                              setPlayerForm({name:p.player_name, role:p.role, credit:parseFloat(p.credit)||9, image:p.image||"", team:p.team});
                              setTab("Players"); // keep on tab but could open modal if existed
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                              toast.info("Editing " + p.player_name);
                            }}
                            style={{ ...S.miniBtn, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
                         >✏️</button>
                      </div>

                      {/* Player Image Area */}
                      <div style={{ height: 180, background: `linear-gradient(180deg, ${COLORS.indigo}10 0%, ${COLORS.indigo}20 100%)`, display: "flex", alignItems: "flex-end", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                         {p.image ? (
                           <img 
                            src={p.image} 
                            alt="" 
                            style={{ width: "90%", height: "90%", objectFit: "contain", filter: "drop-shadow(0 10px 15px rgba(0,0,0,0.3))" }} 
                            onError={e => { e.target.style.display="none"; }}
                           />
                         ) : (
                           <div style={{ fontSize: 60, opacity: 0.2, marginBottom: 20 }}>👤</div>
                         )}
                         <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 40, background: "linear-gradient(to top, rgba(0,0,0,0.5), transparent)" }}></div>
                      </div>

                      {/* Info Area */}
                      <div style={{ padding: 12, textAlign: "center", borderTop: `1px solid ${COLORS.cardBorder}` }}>
                         <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                           {p.player_name}
                         </div>
                         <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.indigoLight }}>{p.team}</span>
                            <span style={{ color: COLORS.textDim, fontSize: 8 }}>•</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted }}>{p.role}</span>
                         </div>
                         <div style={{ marginTop: 8, fontSize: 11, fontWeight: 900, color: COLORS.amber }}>
                            {p.credit} <span style={{ fontSize: 8, color: COLORS.textDim }}>CR</span>
                         </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── LINEUP TAB (Redesigned with Big Cards, Grouping & A-Z Sorting) ── */}
      {tab === "Lineup" && (
        <div style={{ background:"#0f172a", borderRadius:20, padding:30, border:"1px solid #1e293b", boxShadow:"0 20px 25px -5px rgb(0 0 0 / 0.5)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"start", marginBottom:30 }}>
            <div>
              <div style={{ fontSize:22, fontWeight:800, color:"#f8fafc", letterSpacing:"1px", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ background:"#3b82f620", color:"#60a5fa", padding:8, borderRadius:12, fontSize:16 }}>👤</span> SQUAD SELECTION
              </div>
              <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginTop:4, letterSpacing:0.5 }}>{match.team1} VS {match.team2} — SELECT PLAYING XI</div>
            </div>
            <div style={{ display:"flex", gap:12 }}>
              <button onClick={()=>fetchAll()} style={{ background:"transparent", border:"none", color:"#94a3b8", fontSize:13, fontWeight:600, cursor:"pointer" }}>DISCARD</button>
              <button 
                onClick={() => {
                  if (t1Playing < 11 || t2Playing < 11) {
                    toast.warn(`Please select at least 11 players for both teams.`);
                  }
                  setShowPreview(true);
                }} 
                style={{ 
                  background: (t1Playing >= 11 && t2Playing >= 11) ? "#3b82f6" : "#1e293b", 
                  color: (t1Playing >= 11 && t2Playing >= 11) ? "white" : "#64748b", 
                  border:"none", padding:"10px 24px", borderRadius:12, fontWeight:700, fontSize:13, cursor:"pointer",
                  boxShadow: (t1Playing >= 11 && t2Playing >= 11) ? "0 4px 6px -1px rgb(59 130 246 / 0.5)" : "none"
                }}
              >
                PREVIEW LINEUP
              </button>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:40 }}>
            {[ {team:match.team1, list:team1P, count:t1Playing}, {team:match.team2, list:team2P, count:t2Playing} ].map(({team, list, count}) => (
              <div key={team}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20, padding:"0 4px", borderBottom:`2px solid ${COLORS.indigo}30`, paddingBottom:10 }}>
                  <div style={{ fontSize:15, fontWeight:800, color:"#3b82f6", letterSpacing:1 }}>{team.toUpperCase()} POOL</div>
                  <div style={{ fontSize:11, fontWeight:700, color:count >= 11 ? "#22c55e" : "#94a3b8", background:"#1e293b", padding:"4px 10px", borderRadius:20 }}>{count} SELECTED</div>
                </div>
                
                <div style={{ height:700, overflowY:"auto", paddingRight:15 }} className="custom-scrollbar">
                  {["WK", "BAT", "AR", "BOWL"].map(roleKey => {
                    const rolePlayers = list
                      .filter(p => {
                        const r = (p.role || "").toUpperCase();
                        if (roleKey === "WK") return r.includes("WK") || r.includes("WICKET");
                        if (roleKey === "BAT") return r.includes("BAT");
                        if (roleKey === "AR") return r.includes("AR") || r.includes("ALL");
                        if (roleKey === "BOWL") return r.includes("BOWL");
                        return false;
                      })
                      .sort((a, b) => (a.player_name || "").localeCompare(b.player_name || ""));

                    if (rolePlayers.length === 0) return null;

                    const roleTitle = roleKey === "WK" ? "WICKET KEEPERS" : roleKey === "BAT" ? "BATTERS" : roleKey === "AR" ? "ALL ROUNDERS" : "BOWLERS";

                    return (
                      <div key={roleKey} style={{ marginBottom:30 }}>
                        <div style={{ fontSize:10, fontWeight:800, color:COLORS.textDim, marginBottom:12, letterSpacing:1.5 }}>{roleTitle}</div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(130px, 1fr))", gap:12 }}>
                          {rolePlayers.map(p => {
                            const isSelected = p.is_playing;
                            return (
                              <div 
                                key={p.player_id} 
                                onClick={() => togglePlaying(p.player_id, isSelected)}
                                style={{ 
                                  background: isSelected ? "#1e293b" : "#0f172a", 
                                  border: isSelected ? `2px solid ${COLORS.indigo}` : `1px solid #1e293b`,
                                  borderRadius: 16, 
                                  overflow: "hidden",
                                  position: "relative",
                                  cursor: "pointer",
                                  transition: "all 0.2s"
                                }}
                              >
                                {/* Image Area */}
                                <div style={{ height:110, background:`linear-gradient(180deg, ${isSelected ? COLORS.indigo+'30' : 'transparent'} 0%, transparent 100%)`, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
                                  {p.image ? (
                                    <img src={p.image} alt="" style={{ width:"85%", height:"85%", objectFit:"contain" }} />
                                  ) : (
                                    <div style={{ fontSize:40, opacity:0.1, marginBottom:10 }}>👤</div>
                                  )}
                                </div>

                                {/* Info Area */}
                                <div style={{ padding:10, textAlign:"center", background:isSelected ? COLORS.indigo+'15' : 'transparent', borderTop:`1px solid ${COLORS.cardBorder}` }}>
                                  <div style={{ fontSize:13, fontWeight:900, color:isSelected?"white":COLORS.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textTransform:"uppercase" }}>
                                    {p.player_name}
                                  </div>
                                  <div style={{ fontSize:10, fontWeight:700, color:COLORS.textMuted, marginTop:4 }}>{p.credit} CR • {p.role}</div>
                                </div>

                                {/* Selection Checkmark */}
                                {isSelected && (
                                  <div style={{ position:"absolute", top:5, right:5, width:14, height:14, borderRadius:"50%", background:COLORS.indigo, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:"white" }}>✓</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* --- PREVIEW MODAL --- */}
          {showPreview && (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", backdropFilter:"blur(8px)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
              <div style={{ background:"#0f172a", width:"100%", maxWidth:800, borderRadius:24, border:"1px solid #1e293b", padding:30, position:"relative", maxHeight:"90vh", overflowY:"auto" }}>
                <button onClick={()=>setShowPreview(false)} style={{ position:"absolute", top:20, right:20, background:"transparent", border:"none", color:"#94a3b8", fontSize:24, cursor:"pointer" }}>×</button>
                <div style={{ fontSize:24, fontWeight:800, color:"white", marginBottom:10 }}>🔍 Preview Lineup</div>
                <p style={{ color:"#94a3b8", fontSize:13, marginBottom:24 }}>Review the selected playing squad before it goes live on the user app.</p>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
                  {[ {team: match.team1, list: team1P.filter(p=>p.is_playing)}, {team: match.team2, list: team2P.filter(p=>p.is_playing)} ].map(({team, list}) => (
                    <div key={team}>
                      <div style={{ fontSize:14, fontWeight:800, color:"#3b82f6", marginBottom:12, textTransform:"uppercase" }}>{team} ({list.length})</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {list.map(p => (
                          <div key={p.player_id} style={{ display:"flex", justifyContent:"space-between", background:"#1e293b", padding:"8px 12px", borderRadius:10 }}>
                            <span style={{ fontSize:12, fontWeight:600, color:"#f8fafc" }}>
                              {p.player_name} {p.is_captain && <span style={{color:COLORS.amber}}> (C)</span>} {p.is_vice_captain && <span style={{color:COLORS.purple}}> (VC)</span>}
                            </span>
                            <span style={{ fontSize:10, fontWeight:700, color:"#64748b" }}>{p.role}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop:40, display:"flex", gap:16 }}>
                  <button onClick={()=>setShowPreview(false)} style={{ flex:1, padding:16, borderRadius:14, background:"#1e293b", color:"white", border:"none", fontWeight:700, cursor:"pointer" }}>GO BACK</button>
                  <button 
                    disabled={publishing}
                    onClick={publishLineup} 
                    style={{ flex:1, padding:16, borderRadius:14, background:"#22c55e", color:"white", border:"none", fontWeight:800, cursor:publishing?"not-allowed":"pointer", opacity:publishing?0.7:1, boxShadow:"0 10px 15px -3px rgba(34, 197, 94, 0.3)" }}
                  >
                    {publishing ? "⌛ Publishing..." : "CONFIRM & PUBLISH LINEUP 🚀"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SCORE TAB ─────────────────────────────────── */}
      {tab === "Score" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
            {[{key:"team1",team:match.team1,color:COLORS.roleBat},{key:"team2",team:match.team2,color:COLORS.green}].map(({key,team,color})=>(
              <div key={key} style={{...S.card,borderTop:`3px solid ${color}`}}>
                <div style={{fontSize:14,fontWeight:700,color,marginBottom:14}}>{team} Innings</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                  {/* Runs */}
                  <div>
                    <label style={S.label}>Runs</label>
                    <div style={{display:"flex",alignItems:"center",background:COLORS.inputBg,border:`1px solid ${COLORS.cardBorder}`,borderRadius:10,overflow:"hidden"}}>
                      <button type="button" onClick={()=>setScore({...score,[`${key}_runs`]:Math.max(0,(parseInt(score[`${key}_runs`])||0)-1)})} style={{padding:"10px 14px",background:"transparent",border:"none",color:COLORS.text,cursor:"pointer",fontWeight:800}}>-</button>
                      <input className="admin-input-focus" type="number" style={{...S.input,fontSize:20,fontWeight:700,textAlign:"center",border:"none",borderRadius:0,background:"transparent",padding:"8px 0"}} value={score[`${key}_runs`]} onChange={e=>setScore({...score,[`${key}_runs`]:e.target.value})} />
                      <button type="button" onClick={()=>setScore({...score,[`${key}_runs`]:(parseInt(score[`${key}_runs`])||0)+1})} style={{padding:"10px 14px",background:"transparent",border:"none",color:COLORS.text,cursor:"pointer",fontWeight:800}}>+</button>
                    </div>
                    <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                      {[1, 4, 6].map(val => (
                        <button key={val} type="button" onClick={() => setScore({...score, [`${key}_runs`]: (parseInt(score[`${key}_runs`]) || 0) + val})} style={{ flex: 1, padding: "6px 0", background: COLORS.indigo + "15", color: COLORS.indigoLight, border: `1px solid ${COLORS.indigo}30`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.15s" }} className="admin-btn-hover">+{val}</button>
                      ))}
                    </div>
                  </div>
                  {/* Wickets */}
                  <div>
                    <label style={S.label}>Wickets</label>
                    <div style={{display:"flex",alignItems:"center",background:COLORS.inputBg,border:`1px solid ${COLORS.cardBorder}`,borderRadius:10,overflow:"hidden"}}>
                      <button type="button" onClick={()=>setScore({...score,[`${key}_wickets`]:Math.max(0,(parseInt(score[`${key}_wickets`])||0)-1)})} style={{padding:"10px 14px",background:"transparent",border:"none",color:COLORS.text,cursor:"pointer",fontWeight:800}}>-</button>
                      <input className="admin-input-focus" type="number" style={{...S.input,fontSize:20,fontWeight:700,textAlign:"center",border:"none",borderRadius:0,background:"transparent",padding:"8px 0"}} value={score[`${key}_wickets`]} onChange={e=>setScore({...score,[`${key}_wickets`]:e.target.value})} />
                      <button type="button" onClick={()=>setScore({...score,[`${key}_wickets`]:Math.min(10,(parseInt(score[`${key}_wickets`])||0)+1)})} style={{padding:"10px 14px",background:"transparent",border:"none",color:COLORS.text,cursor:"pointer",fontWeight:800}}>+</button>
                    </div>
                  </div>
                  {/* Overs */}
                  <div>
                    <label style={S.label}>Overs</label>
                    <div style={{display:"flex",alignItems:"center",background:COLORS.inputBg,border:`1px solid ${COLORS.cardBorder}`,borderRadius:10,overflow:"hidden"}}>
                      <button type="button" onClick={()=>{
                        let [o,b] = (score[`${key}_overs`]||"0").toString().split(".").map(Number);
                        if(isNaN(o)) o=0; if(isNaN(b)) b=0;
                        if(b===0){ if(o>0){ o-=1; b=5; } } else { b-=1; }
                        setScore({...score,[`${key}_overs`]:`${o}.${b}`});
                      }} style={{padding:"10px 14px",background:"transparent",border:"none",color:COLORS.text,cursor:"pointer",fontWeight:800}}>-</button>
                      <input className="admin-input-focus" type="text" style={{...S.input,fontSize:20,fontWeight:700,textAlign:"center",border:"none",borderRadius:0,background:"transparent",padding:"8px 0"}} value={score[`${key}_overs`]} onChange={e=>setScore({...score,[`${key}_overs`]:e.target.value})} />
                      <button type="button" onClick={()=>{
                        let [o,b] = (score[`${key}_overs`]||"0").toString().split(".").map(Number);
                        if(isNaN(o)) o=0; if(isNaN(b)) b=0;
                        if(b>=5){ o+=1; b=0; } else { b+=1; }
                        setScore({...score,[`${key}_overs`]:`${o}.${b}`});
                      }} style={{padding:"10px 14px",background:"transparent",border:"none",color:COLORS.text,cursor:"pointer",fontWeight:800}}>+</button>
                    </div>
                  </div>
                </div>
                {score[`${key}_runs`]>0&&<div style={{marginTop:10,fontSize:20,fontWeight:800,color:COLORS.text,textAlign:"center"}}>{score[`${key}_runs`]}/{score[`${key}_wickets`]} <span style={{fontSize:13,color:COLORS.textMuted}}>({score[`${key}_overs`]} ov)</span></div>}
              </div>
            ))}
          </div>
          <div style={{...S.card,marginBottom:20}}>
            <div style={S.cardTitle}>Match Winner</div>
            <select className="admin-input-focus" style={{...S.select, marginBottom:20}} value={score.match_winner||""} onChange={e=>setScore({...score, match_winner:e.target.value})}>
              <option value="">-- Select Winner --</option>
              <option value={match.team1}>{match.team1}</option>
              <option value={match.team2}>{match.team2}</option>
              <option value="DRAW">Match Drawn / Tied</option>
            </select>
            
            <div style={S.cardTitle}>Match Status</div>
            <div style={{display:"flex",gap:10}}>
              {["upcoming","live","completed"].map(s=>(
                <button 
                  key={s} 
                  type="button" 
                  disabled={statusUpdating}
                  onClick={()=>setStatus(s)} 
                  style={{flex:1,padding:12,background:match.status===s?COLORS.indigo+"20":COLORS.inputBg,border:`1px solid ${match.status===s?COLORS.indigo+"60":COLORS.cardBorder}`,borderRadius:10,color:match.status===s?COLORS.indigoLight:COLORS.textMuted,fontWeight:700,fontSize:11,cursor:statusUpdating?"not-allowed":"pointer",textTransform:"uppercase", opacity:statusUpdating?0.7:1}}
                >
                  {statusUpdating && match.status === s ? "⌛ Processing..." : s==="live"?"🔴 Live":s==="completed"?"✅ Done":"📅 Upcoming"}
                </button>
              ))}
            </div>
          </div>
          <button className="admin-btn-hover" disabled={saving} onClick={saveScore} style={{...S.btnPrimary,width:"100%",padding:16,fontSize:15,opacity:saving?0.7:1,transition:"all 0.15s"}}>
            {saving?"Updating...":"🚀 Update Score"}
          </button>
        </div>
      )}

      {/* ── POINTS TAB ─────────────────────────────────── */}
      {tab === "Points" && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={S.cardTitle}>Live Player Points</div>
            <button 
              className="admin-btn-hover" 
              disabled={pointsUpdating} 
              onClick={savePoints} 
              style={{ ...S.btnPrimary, background: COLORS.indigo, padding: "10px 20px" }}
            >
              {pointsUpdating ? "⌛ Updating..." : "🚀 Update All Points"}
            </button>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30 }}>
            {[match.team1, match.team2].map(team => (
              <div key={team}>
                <h4 style={{ fontSize: 12, fontWeight: 800, color: COLORS.indigoLight, marginBottom: 15, textTransform: "uppercase" }}>{team}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {players.filter(p => (p.team||"").toUpperCase() === (team||"").toUpperCase() && p.is_playing).map(p => (
                    <div key={p.player_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: COLORS.inputBg, padding: "8px 12px", borderRadius: 12, border: `1px solid ${COLORS.cardBorder}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: COLORS.card, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                          {p.image ? <img src={p.image} style={{ width: "100%", height: "100%", borderRadius: "50%" }} alt="" /> : "👤"}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{p.player_name}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) - 1)} style={{ padding: "4px 10px", background: COLORS.inputBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, color: COLORS.textMuted, cursor: "pointer", fontWeight: 800, fontSize: 11 }}>-1</button>
                          
                          <input 
                            type="number" 
                            value={p.points || 0} 
                            onChange={(e) => handlePlayerPointChange(p.player_id, e.target.value)}
                            style={{ width: 50, background: COLORS.inputBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, padding: "6px", color: "white", textAlign: "center", fontWeight: 800, fontSize: 14 }}
                          />

                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) + 1)} style={{ padding: "4px 10px", background: COLORS.indigo + "15", border: `1px solid ${COLORS.indigo}30`, borderRadius: 6, color: COLORS.indigoLight, cursor: "pointer", fontWeight: 800, fontSize: 11 }}>+1</button>
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) + 5)} style={{ padding: "4px 6px", background: COLORS.indigo+"10", border: `1px solid ${COLORS.indigo}30`, borderRadius: 4, color: COLORS.indigoLight, cursor: "pointer", fontWeight: 700, fontSize: 10 }} title="Boundary (4 runs + 1 bonus)">+5 (4s)</button>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) + 8)} style={{ padding: "4px 6px", background: COLORS.purple+"10", border: `1px solid ${COLORS.purple}30`, borderRadius: 4, color: COLORS.purple, cursor: "pointer", fontWeight: 700, fontSize: 10 }} title="Six (6 runs + 2 bonus) OR Catch">+8 (6s/C)</button>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) + 12)} style={{ padding: "4px 6px", background: COLORS.amber+"10", border: `1px solid ${COLORS.amber}30`, borderRadius: 4, color: COLORS.amber, cursor: "pointer", fontWeight: 700, fontSize: 10 }} title="Stumping / Direct Hit Run Out">+12 (RO)</button>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) + 25)} style={{ padding: "4px 6px", background: COLORS.green+"10", border: `1px solid ${COLORS.green}30`, borderRadius: 4, color: COLORS.green, cursor: "pointer", fontWeight: 700, fontSize: 10 }} title="Wicket (Excluding LBW/Bowled)">+25 (W)</button>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) + 33)} style={{ padding: "4px 6px", background: COLORS.green+"20", border: `1px solid ${COLORS.green}50`, borderRadius: 4, color: COLORS.green, cursor: "pointer", fontWeight: 800, fontSize: 10 }} title="LBW / Bowled (25 + 8 bonus)">+33 (B/L)</button>
                          <button className="admin-btn-hover" onClick={() => handlePlayerPointChange(p.player_id, (parseInt(p.points)||0) - 2)} style={{ padding: "4px 6px", background: COLORS.red+"10", border: `1px solid ${COLORS.red}30`, borderRadius: 4, color: COLORS.red, cursor: "pointer", fontWeight: 700, fontSize: 10 }} title="Dismissal for Duck">-2 (Duck)</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PLAYER STATS TAB (For Predictions) ─────────────────────────────────── */}
      {tab === "Player Stats" && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={S.cardTitle}>Player Match Stats (For Predictions)</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>Enter the actual stats (Runs, 6s, 4s, etc.) for player prediction contests.</div>
            </div>
            <button 
              className="admin-btn-hover" 
              disabled={statsSaving}
              onClick={savePlayerStats} 
              style={{ ...S.btnPrimary, background: COLORS.green, padding: "10px 20px" }}
            >
              {statsSaving ? "⌛ Saving..." : "🚀 Save Stats"}
            </button>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 30 }}>
            {[match.team1, match.team2].map(team => (
              <div key={team}>
                <h4 style={{ fontSize: 13, fontWeight: 800, color: COLORS.indigoLight, marginBottom: 15, textTransform: "uppercase", borderBottom: `1px solid ${COLORS.indigo}30`, paddingBottom: 8 }}>{team}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {players.filter(p => (p.team||"").toUpperCase() === (team||"").toUpperCase() && p.is_playing).map(p => (
                    <div key={p.player_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: COLORS.inputBg, padding: "12px 16px", borderRadius: 12, border: `1px solid ${COLORS.cardBorder}`, flexWrap: "wrap", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 150 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: COLORS.card, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                          {p.image ? <img src={p.image} style={{ width: "100%", height: "100%", borderRadius: "50%" }} alt="" /> : "👤"}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800 }}>{p.player_name}</div>
                          <div style={{ fontSize: 10, color: COLORS.textMuted }}>{p.role}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: 1, justifyContent: "flex-end" }}>
                        {[
                          { label: "Runs", field: "runs" },
                          { label: "4s", field: "fours" },
                          { label: "6s", field: "sixes" },
                          { label: "Wickets", field: "wickets" },
                          { label: "Catches", field: "catches" },
                          { label: "Run Out", field: "run_outs" }
                        ].map(stat => (
                          <div key={stat.field} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                            <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase" }}>{stat.label}</label>
                            <input 
                              type="number" 
                              min="0"
                              placeholder="0"
                              value={p[stat.field] || ""} 
                              onChange={(e) => handlePlayerStatChange(p.player_id, stat.field, e.target.value)}
                              style={{ width: 60, background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: "8px", color: "white", textAlign: "center", fontWeight: 800, fontSize: 14 }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {players.filter(p => (p.team||"").toUpperCase() === (team||"").toUpperCase() && p.is_playing).length === 0 && (
                    <div style={{ fontSize: 12, color: COLORS.textDim, fontStyle: "italic" }}>No playing 11 selected for {team} yet.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CONTESTS TAB ─────────────────────────────────── */}
      {tab === "Contests" && (
        <div>
          <div style={{...S.empty, marginBottom:20}}>
            <div style={{fontSize:13,color:COLORS.textMuted}}>
              {contests.length===0?"No contests for this match yet. Contests are auto-created when the match is published.":`${contests.length} contest(s) linked to this match.`}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
            {contests.map(c=>(
              <div key={c.id||c.contest_id} style={{background:COLORS.card,border:`1px solid ${COLORS.cardBorder}`,borderRadius:14,padding:18}}>
                <div style={{fontSize:15,fontWeight:700,color:COLORS.text,marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>🏆 {c.title||c.name}</span>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>editContest(c)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:13}} title="Edit Contest">✏️</button>
                    <button onClick={()=>deleteContest(c.id||c.contest_id)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:13}} title="Delete Contest">🗑️</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[["Entry",`₹${c.entry_fee}`],[" 1st Prize",`₹${c.first_prize||c.prize_pool}`],["Spots",c.total_spots],["Joined",c.total_joined||0]].map(([l,v])=>(
                    <div key={l} style={{background:COLORS.inputBg,borderRadius:8,padding:8}}>
                      <div style={{fontSize:9,color:COLORS.textMuted,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>{l}</div>
                      <div style={{fontSize:14,fontWeight:800,color:COLORS.text,marginTop:2}}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MatchPanel = () => (
  <ToastProvider><MatchPanelInner /></ToastProvider>
);
export default MatchPanel;
